import React from "react";
import { motion } from "framer-motion";
import { Building2, Trash2, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Provider } from "./types";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";


interface ProviderExclusionsCardProps {
	excludedProviders: Provider[];
	onAddProvider: (provider: Provider) => void;
	onRemoveProvider: (index: number) => void;
	getProviderTypeLabel: (types: string[]) => string;
	inputRef?: React.RefObject<HTMLInputElement>;
}

export default function ProviderExclusionsCard({
	excludedProviders,
	onAddProvider,
	onRemoveProvider,
	getProviderTypeLabel,
	inputRef,
}: ProviderExclusionsCardProps) {
	const { place } = usePlacesAutocomplete({
		inputRef,
		types: ["health"],
		componentRestrictions: { country: "us" },
		fields: ["place_id", "formatted_address", "name", "types", "geometry", "address_components"],
	});

	const handleAddProvider = (place: GooglePlaceResult | null) => {
		if (!place?.place_id || !place?.name) {
			console.warn("Invalid place data received");
			return;
		}

		// Check if provider already exists
		const exists = excludedProviders.some(
			(provider) => provider.place_id === place.place_id,
		);
		if (exists) {
			alert("This provider has already been added to your exclusion list.");
			return;
		}

		// Parse address components
		let city = "";
		let state = "";
		let zip = "";
		place.address_components?.forEach((component) => {
			if (component.types.includes("locality")) {
				city = component.long_name;
			} else if (component.types.includes("administrative_area_level_1")) {
				state = component.short_name;
			} else if (component.types.includes("postal_code")) {
				zip = component.long_name;
			}
		});

		const provider: Provider = {
			name: place.name,
			address: place.formatted_address || "",
			place_id: place.place_id,
			types: place.types || [],
			formatted_address: place.formatted_address || "",
			city,
			state,
			zip,
			latitude: place.geometry?.location.lat() ?? 0,
			longitude: place.geometry?.location.lng() ?? 0,
		};

		onAddProvider(provider);
		// Clear the input field after adding
		if (inputRef && inputRef.current) {
			inputRef.current.value = "";
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6, delay: 0.8 }}
			className="mt-8"
		>
			<Card className="glass-card border-0 shadow-xl">
				<CardHeader className="text-center pb-6">
					<div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
						<Building2 className="h-8 w-8 text-newTheme-orange" />
					</div>
					<CardTitle className="text-2xl font-bold text-black font-karla">
						Provider Exclusions
					</CardTitle>
					<p className="text-newTheme-grey font-karla mt-2 max-w-2xl mx-auto">
						List any healthcare providers or hospitals you prefer not to work
						with. This is completely confidential and helps us match you with
						the right families.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					{/* Add Provider Section */}
					<form onSubmit={(e) => e.preventDefault()} className="space-y-4">
						<Label className="text-sm font-medium text-newTheme-spruce font-karla" htmlFor="exclude-provider-input">
							Search Healthcare Providers
						</Label>
						<div className="relative">
							<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
							<Input
								type="search"
								id="excluded-provider-input"
								ref={inputRef}
								placeholder="Search for hospitals, clinics, or healthcare providers..."
								className="w-full pl-10"
							/>
						</div>
						<Button
							type="submit"
							onClick={() => handleAddProvider(place)}
							size="lg"
							variant="default"
						>Add Provider</Button>
						<p className="text-xs text-gray-500 font-karla">
							Start typing to search for healthcare providers in your area.
							Select a provider to automatically add it to your exclusions list.
						</p>
					</form>

					{/* Current Excluded Providers */}
					<div>
						<div className="flex items-center justify-between mb-4">
							<Label className="text-lg font-semibold text-gray-900 font-karla">
								Your Excluded Providers ({excludedProviders.length})
							</Label>
						</div>

						{excludedProviders.length > 0 ? (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{excludedProviders.map((provider, index) => (
									<motion.div
										key={index}
										initial={{ opacity: 0, x: -20 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: 20 }}
										className="bg-white rounded-lg p-4 border border-gray-200"
									>
										<div className="flex items-start justify-between">
											<div className="flex-1 min-w-0">
												<h4 className="font-semibold text-gray-900 font-karla truncate">
													{provider.name}
												</h4>
												<p className="text-sm text-gray-600 font-karla mt-1 line-clamp-2">
													{provider.formatted_address}
												</p>
												<p className="text-xs text-gray-500 font-karla mt-1">
													{getProviderTypeLabel(provider.types)}
												</p>
											</div>
											<Button
												variant="outline"
												size="sm"
												onClick={() => onRemoveProvider(index)}
												className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-600 ml-3 flex-shrink-0"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</motion.div>
								))}
							</div>
						) : (
							/* Empty State */
							<div className="text-center py-8">
								<div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
									<Building2 className="h-8 w-8 text-gray-400" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900 font-karla mb-2">
									No Excluded Providers
								</h3>
								<p className="text-gray-600 font-karla">
									You haven't excluded any providers yet. You can add providers
									above or this information is optional.
								</p>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
